"use client"

import Link from "next/link"
import Image from 'next/image'
import { useEffect, useId, useRef, useState, type ReactNode } from "react"
import { ArrowLeft, ArrowUpRight, LockKeyhole, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { SocialProfile } from "@/lib/social/types"
import { socialProfilePath } from '@/lib/social/links'
import { SocialProvider, useSocial } from "./client"
import { PeopleSidebar } from "./people"

export const fieldClass = "form-control w-full px-3.5 py-2.5 text-base leading-5 md:text-sm"

export function SocialPage({ title, children, action, rail = true, wide = false, back = false, suggestionKey, flush = false }: { title?: string; description?: string; children: ReactNode; action?: ReactNode; rail?: boolean; wide?: boolean; back?: boolean; suggestionKey?: string; flush?: boolean }) {
  return (
    <SocialProvider>
      <div className={cn("social-page mx-auto grid w-full gap-5 pb-20 md:px-5 md:pt-2 md:pb-6", flush && "social-page-flush", rail ? "max-w-[1120px] xl:max-w-[calc(50%_+_560px)] xl:grid-cols-[minmax(0,1fr)_280px] xl:gap-x-12 2xl:gap-x-16" : wide ? "max-w-[1050px]" : "max-w-[740px]")}>
        <div className={cn("min-w-0", rail && "w-full xl:relative xl:left-4 xl:max-w-[780px] xl:justify-self-center")}>
          {title && <header className="social-page-header sticky top-14 z-20 flex min-h-14 items-center justify-between gap-2 border-b bg-background/95 px-4 py-2 backdrop-blur-md md:static md:mb-2 md:border-0 md:bg-transparent md:px-0">
            <div className="flex min-w-0 items-center gap-1">{back && <Link href="/home" aria-label="Back to feed" className="-ml-2 flex size-11 shrink-0 items-center justify-center rounded-full hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"><ArrowLeft className="size-4" /></Link>}<h1 className="type-page-title truncate">{title}</h1></div>{action}
          </header>}
          <AccountNotice flush={flush} />
          {children}
        </div>
        {rail && <aside className="hidden xl:sticky xl:top-2 xl:block xl:h-[calc(100dvh_-_1rem)] xl:self-start"><PeopleSidebar selectionKey={suggestionKey ?? title ?? 'home'} /></aside>}
      </div>
    </SocialProvider>
  )
}

function AccountNotice({ flush = false }: { flush?: boolean }) {
  const { profile, loading, error, reload } = useSocial()
  if (loading) return null
  if (error) return <ErrorState message={error} retry={reload} />
  if (profile) return null
  return (
    <div className={cn("flex items-center justify-between gap-3 border-b px-5 py-3 text-sm", !flush && "md:mb-4 md:rounded-xl md:border md:bg-card")}>
      <span className="font-medium">Sign in to post and follow.</span>
      <Button asChild size="sm" className="rounded-full"><Link href="/login">Log in <ArrowUpRight /></Link></Button>
    </div>
  )
}

export function AccountGate({ children }: { children: ReactNode }) {
  const { profile, loading, error } = useSocial()
  if (loading) return <LoadingState />
  if (error) return null
  if (!profile) return <EmptyState title="Log in to continue" description="Use your account to post, follow and save." icon={<LockKeyhole className="size-6" />}><Button asChild className="rounded-full"><Link href="/login">Log in</Link></Button></EmptyState>
  return children
}

export function EmptyState({ title, description, icon, children }: { title: string; description: string; icon?: ReactNode; children?: ReactNode }) {
  return <div className="rounded-2xl px-4 py-8 text-center">
    {icon && <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">{icon}</div>}
    <h2 className="type-section-title">{title}</h2>
    <p className="mx-auto mt-2 max-w-sm text-base leading-relaxed text-muted-foreground">{description}</p>
    {children && <div className="mt-4">{children}</div>}
  </div>
}

export function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return <div role="alert" className="my-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-destructive/25 bg-destructive/5 p-4 text-sm">
    <p className="min-w-0 flex-1 break-words">{message}</p>{retry && <Button variant="outline" size="sm" onClick={retry}>Retry</Button>}
  </div>
}

export function LoadingState({ compact = false }: { compact?: boolean }) {
  return <div role="status" aria-label="Loading content" className="space-y-4">
    <span className="sr-only">Loading content...</span>
    {Array.from({ length: compact ? 1 : 3 }, (_, index) => <div key={index} aria-hidden="true" className="space-y-4 rounded-2xl border p-5 motion-safe:animate-pulse">
      <div className="flex items-center gap-3"><div className="size-10 rounded-full bg-muted" /><div className="h-3 w-28 rounded bg-muted" /></div>
      <div className="h-3 w-full rounded bg-muted" /><div className="h-3 w-2/3 rounded bg-muted" />
    </div>)}
  </div>
}

export function Avatar({ profile, large = false, small = false, className }: { profile: SocialProfile; large?: boolean; small?: boolean; className?: string }) {
  const [failedUrl, setFailedUrl] = useState<string>()
  return <span aria-hidden="true" data-profile-avatar className={cn("flex shrink-0 items-center justify-center overflow-hidden bg-muted font-bold uppercase text-foreground ring-2 ring-background", large ? "size-24 text-3xl" : small ? 'size-8 text-[10px]' : "size-11 text-sm", className, 'rounded-[50%]')}>
    {profile.avatarUrl && profile.avatarUrl !== failedUrl ? <Image unoptimized src={profile.avatarUrl} alt="" width={512} height={512} className="size-full object-cover" onError={() => setFailedUrl(profile.avatarUrl!)} /> : (profile.displayName || profile.handle).split(/\s+/).map(part => part[0]).join('').slice(0, 2)}
  </span>
}

export function ProfileIdentity({ profile }: { profile: SocialProfile }) {
  return <Link href={socialProfilePath(profile.handle)} className="flex min-w-0 items-center gap-3 rounded-md focus-visible:outline-2 focus-visible:outline-ring">
    <Avatar profile={profile} /><span className="min-w-0"><span className="block truncate text-sm font-semibold">{profile.displayName}</span><span className="block truncate text-xs text-muted-foreground">@{profile.handle}</span></span>
  </Link>
}

export function Timestamp({ value }: { value: string }) {
  const [now] = useState(() => Date.now())
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const hours = Math.max(0, (now - date.getTime()) / 3600000)
  const label = hours < 1 ? `${Math.max(1, Math.floor(hours * 60))}m` : hours < 24 ? `${Math.floor(hours)}h` : hours < 168 ? `${Math.floor(hours / 24)}d` : date.toLocaleDateString('en', { month: 'short', day: 'numeric' })
  return <time suppressHydrationWarning dateTime={value} title={date.toLocaleString()} className="text-xs text-muted-foreground">{label}</time>
}

// Native dialogs provide focus trapping, Escape handling, and focus restoration.
export function SocialDialog({ title, children, onClose, wide = false, focusSafeAction = false, className }: { title: string; children: ReactNode; onClose: () => void; wide?: boolean; focusSafeAction?: boolean; className?: string }) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  useEffect(() => {
    const dialog = ref.current
    dialog?.showModal()
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => { dialog?.close(); document.body.style.overflow = previous }
  }, [])
  useEffect(() => { if (focusSafeAction) ref.current?.querySelector<HTMLButtonElement>('[data-dialog-safe-action]')?.focus() }, [focusSafeAction])
  return <dialog ref={ref} aria-labelledby={titleId} onCancel={event => { event.preventDefault(); onClose() }} onClick={event => { if (event.target === event.currentTarget) onClose() }} className={cn("social-dialog fixed inset-0 m-auto max-h-[85dvh] w-[calc(100%_-_2rem)] overflow-y-auto rounded-2xl border bg-background p-0 text-foreground shadow-xl backdrop:bg-black/40 backdrop:backdrop-blur-sm", wide ? 'max-w-4xl' : 'max-w-lg', className)}>
    <div data-social-dialog-body className="p-5 sm:p-6"><div data-social-dialog-header className="mb-5 flex items-center justify-between gap-4"><h2 id={titleId} className="type-section-title">{title}</h2><Button autoFocus={!focusSafeAction} type="button" size="icon" variant="ghost" aria-label="Close dialog" onClick={onClose}><X /></Button></div>{children}</div>
  </dialog>
}
